(() => {
  const script = document.currentScript;
  if (!script || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const presets = {
    autumn: { shapes: ['plane-leaf', 'fallen-leaf', 'plane-leaf'], colors: ['#b96543', '#bb8754', '#d09a58'], motion: 'fall' },
    winter: { shapes: ['snow-a', 'snow-b'], colors: ['#c4d4dc', '#e8f0f2', '#afc5d1'], motion: 'snow' },
    newyear: { shapes: ['star', 'sparkle', 'confetti'], colors: ['#d8b865', '#e9ded0', '#b7a896'], motion: 'glimmer' },
    spring: { shapes: ['flower', 'spring-leaves'], colors: ['#ecb6c7', '#6d9e75'], motion: 'fall' },
    summer: { shapes: ['sun', 'sparkle', 'sparkle', 'sparkle'], colors: ['#edbb58', '#f7df9f', '#eab878'], motion: 'glimmer' },
    mothers: { shapes: ['flower', 'petal'], colors: ['#eeb7bf', '#f2d1ca', '#d49da9'], motion: 'fall' },
    fathers: { shapes: ['compass', 'stitch'], colors: ['#496477', '#baaa90', '#788b91'], motion: 'float' },
    school: { shapes: ['plane', 'confetti'], colors: ['#5a8290', '#c59a62', '#bc7064'], motion: 'float' },
    sale: { shapes: ['tag', 'confetti', 'sparkle'], colors: ['#1d1d1b', '#d8b769', '#f0dbad'], motion: 'fall' },
    valentines: { shapes: ['heart', 'heart-outline', 'heart'], colors: ['#c95070', '#e98da5', '#f2afbe'], motion: 'fall' }
  };
  const effect = presets[script.dataset.effect] ? script.dataset.effect : 'autumn';
  const preset = presets[effect];
  const repeatMode = ['hours', 'session', 'alternate', 'page'].includes(script.dataset.repeatMode)
    ? script.dataset.repeatMode : 'hours';
  const repeatHours = Math.min(72, Math.max(1, Number(script.dataset.repeatHours) || 24));
  const designMode = script.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
  const storageKey = `muya:seasonal:${effect}:${repeatMode}:v1`;
  const namespace = 'http://www.w3.org/2000/svg';
  const sceneDuration = 4800;
  let activeCleanup;

  const storageRead = (kind, key) => {
    try { return window[kind]?.getItem(key) ?? null; } catch (_) { return null; }
  };
  const storageWrite = (kind, key, value) => {
    try { window[kind]?.setItem(key, String(value)); } catch (_) { /* Private modes can block storage. */ }
  };

  const shouldPlay = () => {
    if (designMode || repeatMode === 'page') return true;
    if (repeatMode === 'hours') {
      const storedTime = storageRead('localStorage', storageKey);
      const lastPlayed = Number(storedTime);
      return storedTime === null || !Number.isFinite(lastPlayed) || Date.now() - lastPlayed >= repeatHours * 60 * 60 * 1000;
    }
    if (storageRead('sessionStorage', storageKey)) return false;
    if (repeatMode === 'session') return true;
    const count = Number(storageRead('localStorage', `${storageKey}:count`)) || 0;
    return count % 2 === 0;
  };

  const markPlayed = () => {
    if (designMode || repeatMode === 'page') return;
    if (repeatMode === 'hours') {
      storageWrite('localStorage', storageKey, Date.now());
      return;
    }
    storageWrite('sessionStorage', storageKey, '1');
    if (repeatMode === 'alternate') {
      const countKey = `${storageKey}:count`;
      const count = Number(storageRead('localStorage', countKey)) || 0;
      storageWrite('localStorage', countKey, count + 1);
    }
  };

  const markSkippedAlternateVisit = () => {
    if (repeatMode !== 'alternate' || storageRead('sessionStorage', storageKey)) return;
    storageWrite('sessionStorage', storageKey, '1');
    const countKey = `${storageKey}:count`;
    const count = Number(storageRead('localStorage', countKey)) || 0;
    storageWrite('localStorage', countKey, count + 1);
  };

  const shapes = {
    'plane-leaf': '<path d="M50 7 C46 19 42 27 35 34 C30 29 24 26 17 25 C18 34 19 40 23 46 C17 44 11 45 6 49 C12 57 19 62 28 62 C27 69 30 75 35 79 C41 74 46 68 49 62 L46 96 L54 96 L53 62 C57 68 62 74 68 79 C73 75 76 69 75 62 C84 62 91 57 96 49 C91 45 85 44 79 46 C83 40 84 34 85 25 C78 26 72 29 67 34 C60 27 56 19 50 7 Z" fill="currentColor"/><path d="M51 82 51 28 M50 43 20 28 M50 55 9 49 M50 67 35 77 M52 43 80 28 M52 55 92 49 M52 67 68 77" fill="none" stroke="#623d2c" stroke-opacity=".5" stroke-width="5" stroke-linecap="round"/>',
    'fallen-leaf': '<path d="M42 77 C20 73 11 54 19 37 C27 20 48 21 72 10 C78 34 73 55 57 68 C52 73 47 76 42 77 Z" fill="currentColor"/><path d="M42 77 72 12 M52 57 30 42 M58 44 44 31 M52 57 69 51 M58 44 69 35" fill="none" stroke="#623d2c" stroke-opacity=".5" stroke-width="5" stroke-linecap="round"/><path d="M42 77 33 91" fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round"/><path d="M64 81 C66 64 79 56 94 52 C92 72 80 84 64 81 Z" fill="currentColor" opacity=".85"/><path d="M64 81 92 53 M67 74 79 74" fill="none" stroke="#623d2c" stroke-opacity=".5" stroke-width="5" stroke-linecap="round"/>',
    'spring-leaves': '<path d="M15 70 C16 45 31 26 60 22 C58 47 41 66 15 70 Z" fill="currentColor"/><path d="M44 77 C50 55 69 43 91 41 C87 64 71 78 44 77 Z" fill="currentColor" opacity=".84"/><path d="M10 87 15 70 C35 64 60 52 91 41 M15 70 58 24 M44 77 89 43" fill="none" stroke="#315b3c" stroke-opacity=".6" stroke-width="5" stroke-linecap="round"/>',
    'snow-a': '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="5"><path d="M50 7v86M7 50h86M20 20l60 60M80 20 20 80"/><path d="m38 18 12 11 12-11M38 82l12-11 12 11M18 38l11 12-11 12M82 38 71 50l11 12" stroke-width="3"/></g>',
    'snow-b': '<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="5"><path d="M50 8v84M14 29l72 42M14 71l72-42"/><path d="m40 19 10 11 10-11M40 81l10-11 10 11M25 26l4 14-14 3M75 74l-4-14 14-3M25 74l4-14-14-3M75 26l-4 14 14 3" stroke-width="3"/></g>',
    star: '<path d="M50 4 60 39 96 50 60 60 50 96 39 60 4 50 39 39Z" fill="currentColor"/>',
    sparkle: '<path d="M50 2 56 43 98 50 56 57 50 98 43 57 2 50 43 43Z" fill="currentColor"/><circle cx="17" cy="17" r="5" fill="currentColor"/>',
    sun: '<circle cx="50" cy="50" r="24" fill="currentColor"/><path d="M50 3v16M50 81v16M3 50h16M81 50h16M17 17l11 11M72 72l11 11M83 17 72 28M28 72 17 83" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="7"/>',
    flower: '<g fill="currentColor"><ellipse cx="50" cy="28" rx="13" ry="21"/><ellipse cx="50" cy="28" rx="13" ry="21" transform="rotate(72 50 50)"/><ellipse cx="50" cy="28" rx="13" ry="21" transform="rotate(144 50 50)"/><ellipse cx="50" cy="28" rx="13" ry="21" transform="rotate(216 50 50)"/><ellipse cx="50" cy="28" rx="13" ry="21" transform="rotate(288 50 50)"/></g><circle cx="50" cy="50" r="12" fill="#f2dba2"/>',
    petal: '<path d="M49 88C22 66 17 39 42 13 73 25 83 54 49 88Z" fill="currentColor"/>',
    compass: '<path d="M50 3 61 39 97 50 61 61 50 97 39 61 3 50 39 39Z" fill="currentColor"/><circle cx="50" cy="50" r="7" fill="#f4f0e8"/>',
    stitch: '<path d="M8 50h84" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="8" stroke-dasharray="5 12"/>',
    plane: '<path d="M5 38 95 8 65 91 49 58 5 38Z" fill="currentColor"/><path d="M49 58 95 8 25 49" fill="none" stroke="#ffffff" stroke-opacity=".55" stroke-width="3"/>',
    tag: '<path d="M6 17H57L95 50 57 83H6Z" fill="currentColor"/><circle cx="21" cy="50" r="6" fill="#f8f3e8"/><path d="m45 64 24-28M47 39h3M66 61h3" stroke="#f8f3e8" stroke-width="5" stroke-linecap="round"/>',
    confetti: '<path d="M30 10 69 21 58 91 20 78Z" fill="currentColor"/>',
    heart: '<path d="M50 89 15 55C-2 38 8 11 30 11c10 0 17 5 20 13 3-8 10-13 20-13 22 0 32 27 15 44Z" fill="currentColor"/>',
    'heart-outline': '<path d="M50 89 15 55C-2 38 8 11 30 11c10 0 17 5 20 13 3-8 10-13 20-13 22 0 32 27 15 44Z" fill="none" stroke="currentColor" stroke-width="8" stroke-linejoin="round"/>'
  };

  const makeParticle = (index, width) => {
    const svg = document.createElementNS(namespace, 'svg');
    const shape = preset.shapes[index % preset.shapes.length];
    const color = preset.colors[index % preset.colors.length];
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('muya-seasonal-particle');
    svg.innerHTML = shapes[shape];
    svg.style.color = color;
    const size = shape === 'sun'
      ? (width < 768 ? 42 + Math.random() * 13 : 50 + Math.random() * 18)
      : (width < 768 ? 16 + Math.random() * 21 : 17 + Math.random() * 29);
    svg.style.setProperty('--muya-size', `${size}px`);
    // Keep the decorative fall on the right edge instead of sweeping across content.
    svg.style.setProperty('--muya-left', `${(width < 768 ? 68 : 79) + Math.random() * (width < 768 ? 30 : 20)}vw`);
    svg.style.setProperty('--muya-drift', `${-(4 + Math.random() * (width < 768 ? 11 : 15))}vw`);
    svg.style.setProperty('--muya-duration', `${3.6 + Math.random() * .6}s`);
    svg.style.setProperty('--muya-delay', `${Math.random() * .5}s`);
    svg.style.setProperty('--muya-opacity', `${.25 + Math.random() * .25}`);
    svg.style.setProperty('--muya-start-rotate', `${(Math.random() - .5) * 90}deg`);
    svg.style.setProperty('--muya-end-rotate', `${(Math.random() - .5) * 200}deg`);
    return svg;
  };

  const play = (forcePreview = false) => {
    if (activeCleanup) activeCleanup();
    if (!forcePreview && !shouldPlay()) {
      if (!designMode) markSkippedAlternateVisit();
      return;
    }
    const root = document.createElement('div');
    root.id = 'muya-seasonal-effect';
    root.dataset.motion = preset.motion;
    root.style.setProperty('--muya-scene-duration', `${sceneDuration}ms`);
    root.setAttribute('aria-hidden', 'true');
    const width = window.innerWidth;
    const count = effect === 'summer' ? (width < 768 ? 10 : 17) : width < 768 ? 19 : width < 1200 ? 25 : 32;
    for (let index = 0; index < count; index += 1) root.appendChild(makeParticle(index, width));
    document.body.appendChild(root);
    if (!forcePreview) markPlayed();

    let remaining = sceneDuration;
    let startedAt = performance.now();
    let timer;
    let paused = document.hidden;
    if (paused) root.classList.add('is-paused');
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      root.remove();
      if (activeCleanup === cleanup) activeCleanup = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        if (paused) return;
        paused = true;
        remaining = Math.max(0, remaining - (performance.now() - startedAt));
        clearTimeout(timer);
        root.classList.add('is-paused');
      } else {
        if (!paused) return;
        paused = false;
        startedAt = performance.now();
        root.classList.remove('is-paused');
        clearTimeout(timer);
        timer = setTimeout(cleanup, Math.max(0, remaining));
      }
    };
    if (!paused) timer = setTimeout(cleanup, remaining);
    document.addEventListener('visibilitychange', onVisibility);
    activeCleanup = cleanup;
  };

  const startWhenVisible = () => {
    if (document.hidden) {
      const onVisible = () => {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVisible);
        play();
      };
      document.addEventListener('visibilitychange', onVisible);
    } else {
      play();
    }
  };

  if (document.body) startWhenVisible();
  else document.addEventListener('DOMContentLoaded', startWhenVisible, { once: true });
  if (designMode) document.addEventListener('shopify:section:load', () => play(true));
})();
